import { type ChainType, chainSettings, defaultNativeAddress } from "@/constants/chain-specific/chains";
import type { TokenConfigType } from "@/constants/types/currency";
import { delay } from "@/util/delay";

const MAX_ADDRESSES_PER_CALL = 25;
const MAX_CALLS_PER_MINUTE = 5;
const DELAY_BETWEEN_BATCHES = (60 / MAX_CALLS_PER_MINUTE) * 1000; // in milliseconds

interface TokenPriceChange {
  m5: number;
  h1: number;
  h6: number;
  h24: number;
}

interface TokenVolume {
  m5: number;
  h1: number;
  h6: number;
  h24: number;
}

interface TokenTransactions {
  m5: {
    buys: number;
    sells: number;
    buyers: number;
    sellers: number;
  };
  m15: {
    buys: number;
    sells: number;
    buyers: number;
    sellers: number;
  };
  m30: {
    buys: number;
    sells: number;
    buyers: number;
    sellers: number;
  };
  h1: {
    buys: number;
    sells: number;
    buyers: number;
    sellers: number;
  };
  h24: {
    buys: number;
    sells: number;
    buyers: number;
    sellers: number;
  };
}

export interface TokenStats {
  price: number;
  priceChange: TokenPriceChange;
  volume: TokenVolume;
  transactions: TokenTransactions;
  fdvUsd: number;
  totalReserveInUsd: number;
  totalSupply: number;
  coinGeckoId: string;
  image_url?: string;
  
}

export async function getTokenStats(chain: ChainType, tokens: TokenConfigType[]): Promise<Record<string, TokenStats>> {
  const stats: Record<string, TokenStats> = {};

  const base = chainSettings[chain as ChainType].tokenPriceEndpoints?.geckoStats;

  const fetchStats = async (addresses: string[]) => {
    // Filter out defaultNativeAddress and replace with wrapped native token address
    const filteredAddresses = addresses.map(addr =>
      addr.toLowerCase() === defaultNativeAddress.toLowerCase()
        ? chainSettings[chain as ChainType].wNative.address
        : addr
    );

    const contractList = filteredAddresses.join(",");
    const endpoint = `${base}${contractList}?include=top_pools`;
    try {
      const response = await fetch(endpoint);
      if (response.ok) {
        const data = await response.json();

        // Process each token in the response
        for (const token of data.data) {
          const tokenAddress = token.attributes.address.toLowerCase();

          // Find the pool using the relationships data
          const poolId = token.relationships.top_pools?.data?.[0]?.id;
          if (!poolId) {
            console.warn(`No pool found for token ${tokenAddress}`);
            continue;
          }

          const pool = data.included.find((p: any) => p.id === poolId);
          if (!pool) {
            console.warn(`Pool data not found for token ${tokenAddress}`);
            continue;
          }

          try {
            stats[tokenAddress] = {
              price: Number(token.attributes.price_usd),
              priceChange: {
                m5: Number(pool.attributes.price_change_percentage.m5),
                h1: Number(pool.attributes.price_change_percentage.h1),
                h6: Number(pool.attributes.price_change_percentage.h6),
                h24: Number(pool.attributes.price_change_percentage.h24)
              },
              volume: {
                m5: Number(pool.attributes.volume_usd.m5),
                h1: Number(pool.attributes.volume_usd.h1),
                h6: Number(pool.attributes.volume_usd.h6),
                h24: Number(pool.attributes.volume_usd.h24)
              },
              transactions: pool.attributes.transactions,
              fdvUsd: Number(token.attributes.fdv_usd),
              totalReserveInUsd: Number(token.attributes.total_reserve_in_usd),
              totalSupply: Number(token.attributes.total_supply),
              coinGeckoId: token.attributes.coingecko_id
            };
          } catch (error) {
            console.error(`Error processing data for token ${tokenAddress}:`, error);
          }
        }
      } else {
        throw new Error(`Failed to fetch token stats: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error fetching token stats:', error);
    }
  };

  const addressBatches = [];
  for (let i = 0; i < tokens.length; i += MAX_ADDRESSES_PER_CALL) {
    addressBatches.push(tokens.slice(i, i + MAX_ADDRESSES_PER_CALL).map((token) => token.address));
  }

  for (let i = 0; i < addressBatches.length; i++) {
    if (i > 0 && i % MAX_CALLS_PER_MINUTE === 0) {
      await delay(DELAY_BETWEEN_BATCHES);
    }
    await fetchStats(addressBatches[i]);
  }

  return stats;
}